import * as stylex from '@stylexjs/stylex';
import { styles } from './styles.ts';
import { useEffect, useMemo, useState } from 'react';
import './styles.css';
import { IUser, useFetchUsers } from '@shared/hooks/useFetchUsers.ts';
import { InvitedParticipants } from '@widgets/segments-tab';
import { formatLocalDate } from '@shared/libs/format-local-date.ts';
import { DateRange, IFreelanceEmployee } from '@shared/types/types.ts';
import { ITab, Tabs } from '@shared/ui/tabs/index.tsx';
import { useGetEmployeesByDepartments } from './model/useFetchEmployeesTree.ts';
import { FilterParticipants } from '@features/participants';
import { mapRemoteFilterToValue, REMOTE_FILTERS } from '@entities/participants';
import { ParticipantsListTab } from './ui/participants-list-tab';
import { ParticipantsDepartmentsTab } from './ui/participants-departments-tab';

interface CompaniesStructureProps {
  invitedParticipants: InvitedParticipants[];
  setSelectedParticipants: (users: number[]) => void;
  selectedParticipants: number[];
  setParticipantsDelete?: React.Dispatch<React.SetStateAction<InvitedParticipants[]>>;
  setEditableEmployee?: (employee: IFreelanceEmployee) => void;
  participantsDelete?: InvitedParticipants[];
  source?: string;
  showFilter?: boolean;
}

interface Participant {
  id: number;
  firstName: string;
  lastName: string;
}

const TAB_IDS = {
  LIST: 1,
  DEPARTMENTS: 2,
};

const tabItems: ITab[] = [
  { id: TAB_IDS.LIST, title: 'Список' },
  { id: TAB_IDS.DEPARTMENTS, title: 'Подразделения' },
];
const CompaniesStructure: React.FC<CompaniesStructureProps> = ({
  setSelectedParticipants,
  invitedParticipants = [],
  selectedParticipants,
  setParticipantsDelete,
  setEditableEmployee,
  participantsDelete,
  source = '1cuu',
  showFilter = true,
}) => {
  const [dateRange, setDateRange] = useState<DateRange>([null, null]);
  const joinedAfter = dateRange[0] ? formatLocalDate(dateRange[0], 'T00:00:00') : undefined;
  const joinedBefore = dateRange[1] ? formatLocalDate(dateRange[1], 'T23:59:59') : undefined;
  const [remoteFilter, setRemoteFilter] = useState<REMOTE_FILTERS>(REMOTE_FILTERS.ALL);
  const [activeTab, setActiveTab] = useState<ITab>(tabItems[0]);
  const [inputText, setInputText] = useState('');
  const [debouncedSearchString, setDebouncedSearchString] = useState('');
  const [filteredData, setFilteredData] = useState<IUser[]>([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchString(inputText.trim());
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [inputText]);

  const { userData, fetching: fetchingAllUsers } = useFetchUsers(
    joinedAfter,
    joinedBefore,
    source,
    mapRemoteFilterToValue(remoteFilter),
    activeTab.id === TAB_IDS.LIST && debouncedSearchString ? debouncedSearchString : undefined,
    activeTab.id !== TAB_IDS.LIST
  );

  const { data, fetching: fetchingDepartments } = useGetEmployeesByDepartments(
    joinedAfter,
    joinedBefore,
    undefined,
    mapRemoteFilterToValue(remoteFilter),
    activeTab.id === TAB_IDS.DEPARTMENTS && debouncedSearchString ? debouncedSearchString : undefined,
    activeTab.id !== TAB_IDS.DEPARTMENTS
  );

  const isGuest = source === 'guest';

  const tabsToShow = useMemo(() => {
    return isGuest ? [] : tabItems;
  }, [isGuest]);

  useEffect(() => {
    if (userData && userData.participants) {
      setFilteredData(userData.participants);
    }
  }, [userData]);

  const handleCheck = async (participant: Participant) => {
    const invitedParticipant = invitedParticipants.find((el) => el.userId === participant.id);
    if (invitedParticipant) {
      if (participantsDelete?.find((el) => el.userId === participant.id)) {
        setParticipantsDelete?.(participantsDelete.filter((elem) => elem.userId !== participant.id));
      } else {
        setParticipantsDelete?.([...(participantsDelete || []), invitedParticipant]);
      }
    } else {
      if (selectedParticipants.includes(participant.id)) {
        setSelectedParticipants(selectedParticipants.filter((el) => el !== participant.id));
      } else {
        setSelectedParticipants([...selectedParticipants, participant.id]);
      }
    }
  };

  const statusAllChecked = filteredData.every(
    (participant: Participant) =>
      invitedParticipants.find(
        (el) => el.userId === participant.id && !participantsDelete?.find((elem) => elem.userId === el.userId)
      ) || selectedParticipants.includes(participant.id)
  );

  const getParticipantCheckedStatus = (participantId: number) => {
    const isInvitedParticipant = invitedParticipants.some(({ userId }) => userId === participantId);

    if (isInvitedParticipant) {
      return !participantsDelete?.find(({ userId }) => userId === participantId);
    }

    return selectedParticipants.includes(participantId);
  };

  const handleAllCheck = () => {
    const visibleUserIds = filteredData.map((participant: Participant) => participant.id);

    if (statusAllChecked) {
      setSelectedParticipants(selectedParticipants.filter((id) => !visibleUserIds.includes(id)));

      const participantsToRemove = invitedParticipants.filter((participant) =>
        visibleUserIds.includes(participant.userId)
      );
      setParticipantsDelete?.([...(participantsDelete || []), ...participantsToRemove]);
    } else {
      const newCheckedUsers = visibleUserIds.filter(
        (id) => !invitedParticipants.some((invited) => invited.userId === id) && !selectedParticipants.includes(id)
      );
      setSelectedParticipants([...selectedParticipants, ...newCheckedUsers]);

      participantsDelete &&
        setParticipantsDelete?.(
          participantsDelete?.filter((participant) => !visibleUserIds.includes(participant.userId))
        );
    }
  };

  const handleInput = (text: string) => {
    setInputText(text);
  };

  const selectedParticipantsCount = [
    ...new Set([
      ...invitedParticipants.filter((id) => !participantsDelete?.includes(id)).map((el) => el.userId),
      ...selectedParticipants,
    ]),
  ];

  const onEditEmployee = (e: React.MouseEvent<HTMLButtonElement>, freelanceEmployee: IFreelanceEmployee) => {
    e.stopPropagation();
    setEditableEmployee?.(freelanceEmployee);
  };

  const departmentTree = useMemo(() => data?.getEmployeesByDepartments ?? [], [data?.getEmployeesByDepartments]);

  return (
    <div {...stylex.props(styles.container, styles.loaderContainer)}>
      <div {...stylex.props(styles.modalContainer(isGuest))}>
        <div {...stylex.props(styles.content)}>
          <FilterParticipants
            inputText={inputText}
            onChangeSearch={handleInput}
            dateRange={dateRange}
            onChangeDates={setDateRange}
            remoteFilter={remoteFilter}
            onChangeRemoteFilter={setRemoteFilter}
            showFilter={showFilter}
          />

          <div {...stylex.props(styles.participantsColumn)}>
            <div {...stylex.props(styles.inputWithSelectedParticipant)}>
              <div {...stylex.props(styles.tabsContainer)}>
                <Tabs tabs={tabsToShow} activeTab={activeTab} onChange={setActiveTab} size="medium" />

                <p {...stylex.props(styles.participantCount)}>Выбрано: {selectedParticipantsCount.length}</p>
              </div>
            </div>

            {activeTab.id === TAB_IDS.LIST && (
              <ParticipantsListTab
                participants={filteredData}
                isFetching={fetchingAllUsers}
                searchString={debouncedSearchString}
                isGuest={isGuest}
                statusAllChecked={statusAllChecked}
                onCheck={handleCheck}
                onAllCheck={handleAllCheck}
                getParticipantCheckedStatus={getParticipantCheckedStatus}
                onEditEmployee={isGuest ? onEditEmployee : undefined}
              />
            )}
            {activeTab.id === TAB_IDS.DEPARTMENTS && (
              <ParticipantsDepartmentsTab
                departmentTree={departmentTree}
                isFetching={fetchingDepartments}
                searchString={debouncedSearchString}
                selectedParticipants={selectedParticipants}
                invitedParticipants={invitedParticipants}
                participantsDelete={participantsDelete}
                statusAllChecked={statusAllChecked}
                onAllCheck={handleAllCheck}
                setSelectedParticipants={setSelectedParticipants}
                setParticipantsDelete={setParticipantsDelete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompaniesStructure;
